(() => {
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const normalize = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[×″"']/g, ' ').replace(/\s+/g, ' ').trim();
  class ZveltComparison {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse(root.querySelector('[data-zc-data]').textContent);
      this.products = this.data.products.filter(p => p.spec && p.variants.length);
      this.byId = new Map(this.products.map(p => [p.id, p]));
      this.app = root.querySelector('[data-zc-app]');
      this.dialog = root.querySelector('[data-zc-dialog]');
      this.bridge = root.querySelector('[data-zc-bridge]');
      this.selected = [];
      this.variants = new Map();
      this.unit = 'in';
      this.differences = false;
      this.filter = 'all';
      this.query = '';
      this.formatter = new Intl.NumberFormat(this.data.locale === 'fr' ? 'fr-CA' : 'en-CA', {style:'currency',currency:this.data.currency,currencyDisplay:'narrowSymbol'});
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      root.addEventListener('click', this.onClick);
      root.addEventListener('change', this.onChange);
      root.querySelector('[data-zc-search]').addEventListener('input', e => { this.query = e.target.value; this.renderPicker(); });
      this.dialog.addEventListener('click', e => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => this.root.querySelector('[data-zc-open]')?.focus({preventScroll:true}));
      document.addEventListener('theme4:compare:update', () => {
        if (this.saving) return;
        queueMicrotask(() => { this.readSelection(); this.render(); });
      });
      window.addEventListener('storage', e => {
        if (e.key !== 'theme4:compare:id') return;
        const ids = this.safeIds((e.newValue || '').split(','));
        if (Array.isArray(this.bridge.array)) this.bridge.array.splice(0, this.bridge.array.length, ...ids);
        this.selected = ids;
        this.render();
      });
      this.start().catch(() => this.fail());
    }
    t(key, vars = {}) {
      return Object.entries(vars).reduce((s, [k,v]) => s.replaceAll(`{${k}}`, String(v)), this.data.strings[key] || key);
    }
    money(cents) { return this.formatter.format(cents / 100); }
    fail() { this.app.hidden = true; this.root.querySelector('[data-zc-fallback]').hidden = false; }
    name(p) { return p.spec.kind === 'shelf' || p.spec.kind === 'wc' ? this.t(p.spec.kind) : p.spec.name; }
    format(p) { return p.spec.format.replace('.', this.data.locale === 'fr' ? ',' : '.') + '″'; }
    safeIds(ids) { return [...new Set(ids.map(String))].filter(id => this.byId.has(id)).slice(0,5); }
    readSelection() {
      let ids = this.bridge.array;
      if (!Array.isArray(ids)) {
        try { ids = (localStorage.getItem('theme4:compare:id') || '').split(','); } catch { ids = []; }
      }
      this.selected = this.safeIds(ids);
    }
    async start() {
      let timer;
      await Promise.race([customElements.whenDefined('hdt-compare'), new Promise(resolve => { timer = setTimeout(resolve,5000); })]);
      clearTimeout(timer);
      this.readSelection();
      try {
        const saved = JSON.parse(sessionStorage.getItem('zvelt:compare:preferences') || '{}');
        this.unit = saved.unit === 'mm' ? 'mm' : 'in';
        this.differences = !!saved.differences;
        for (const [id, variant] of Object.entries(saved.variants || {})) {
          if (this.byId.get(id)?.variants.some(v => v.id === variant)) this.variants.set(id, variant);
        }
      } catch {}
      document.title = this.t('page_title');
      this.root.dataset.ready = 'true';
      this.render();
    }
    savePreferences() {
      try { sessionStorage.setItem('zvelt:compare:preferences', JSON.stringify({unit:this.unit,differences:this.differences,variants:Object.fromEntries(this.variants)})); } catch {}
    }
    commit(ids) {
      const next = this.safeIds(ids);
      if (document.getElementById('hdt_compare_list')) {
        const add = next.find(id => !this.selected.includes(id));
        const remove = this.selected.find(id => !next.includes(id));
        const id = add || remove;
        if (!id) return;
        const template = document.createElement('template');
        template.innerHTML = `<hdt-compare hidden data-id="${escape(id)}" data-handle="${escape(this.byId.get(id).handle)}" action="${add ? 'add' : 'remove'}"><span></span><span></span></hdt-compare>`;
        const button = template.content.firstElementChild;
        this.root.append(button);
        button.click();
        return;
      }
      this.selected = next;
      if (Array.isArray(this.bridge.array)) this.bridge.array.splice(0, this.bridge.array.length, ...next);
      let stored = true;
      try { localStorage.setItem('theme4:compare:id', next.join(',')); } catch { stored = false; }
      this.saving = true;
      document.querySelectorAll('hdt-compare[data-id]').forEach(button => {
        if (button.hasAttribute('remove-on-page')) return;
        button.setAttribute('action', next.includes(button.dataset.id) ? (button.actionAfterAdded || 'added') : 'add');
      });
      document.dispatchEvent(new CustomEvent('theme4:compare:update', {bubbles:true,detail:'the4'}));
      this.saving = false;
      this.render();
      this.announce(stored ? this.t('updated',{count:next.length}) : this.t('error'));
    }
    variant(p) { return p.variants.find(v => v.id === this.variants.get(p.id)) || p.variants.find(v => v.id === p.defaultVariant) || p.variants[0]; }
    url(p, threeD = false) { return `${p.url}?variant=${encodeURIComponent(this.variant(p).id)}${threeD ? '#zvelt-3d' : ''}`; }
    measure(inches) {
      if (this.unit === 'mm') return new Intl.NumberFormat(this.data.locale,{maximumFractionDigits:1}).format(Number((inches * 25.4).toFixed(6))) + ' mm';
      if (inches < 1) return new Intl.NumberFormat(this.data.locale,{maximumFractionDigits:2}).format(inches) + ' ' + this.t('inches');
      const whole = Math.floor(inches), numerator = Math.round((inches - whole) * 16);
      if (!numerator) return whole + ' ' + this.t('inches');
      const gcd = (a,b) => b ? gcd(b,a % b) : a;
      const d = gcd(numerator,16);
      return `${whole} ${numerator / d}/${16 / d} ${this.t('inches')}`;
    }
    announce(text) { this.root.querySelector('[data-zc-status]').textContent = text; }
    openPicker() {
      this.renderPicker();
      this.dialog.showModal();
      this.root.querySelector('[data-zc-search]').focus();
    }
    onClick(event) {
      const target = event.target.closest('button');
      if (!target) return;
      if (target.hasAttribute('data-zc-open')) this.openPicker();
      if (target.hasAttribute('data-zc-close')) this.dialog.close();
      if (target.dataset.zcRemove) {
        this.commit(this.selected.filter(id => id !== target.dataset.zcRemove));
        this.root.querySelector('[data-zc-open]')?.focus({preventScroll:true});
      }
      if (target.dataset.zcToggle) {
        const id = target.dataset.zcToggle;
        if (this.selected.includes(id)) this.commit(this.selected.filter(x => x !== id));
        else if (this.selected.length < 5) this.commit([...this.selected,id]);
        this.root.querySelector(`[data-zc-toggle="${id}"]`)?.focus({preventScroll:true});
      }
      if (target.dataset.zcFilter) { this.filter = target.dataset.zcFilter; this.renderPicker(); this.root.querySelector(`[data-zc-filter="${this.filter}"]`)?.focus(); }
      if (target.hasAttribute('data-zc-reset-search')) {
        this.query = ''; this.filter = 'all'; this.root.querySelector('[data-zc-search]').value = ''; this.renderPicker();
        this.root.querySelector('[data-zc-search]').focus();
      }
      if (target.dataset.zcUnit) { this.unit = target.dataset.zcUnit; this.savePreferences(); this.render(); this.root.querySelector(`[data-zc-unit="${this.unit}"]`)?.focus({preventScroll:true}); }
    }
    onChange(event) {
      const el = event.target;
      if (el.hasAttribute('data-zc-differences')) { this.differences = el.checked; this.savePreferences(); this.render(); this.root.querySelector('[data-zc-differences]')?.focus({preventScroll:true}); }
      if (el.dataset.zcVariant) {
        this.variants.set(el.dataset.zcVariant, el.value); this.savePreferences(); this.render();
        this.root.querySelector(`[data-zc-variant="${el.dataset.zcVariant}"]`)?.focus({preventScroll:true});
        this.announce(this.t('price_note'));
      }
      if (el.hasAttribute('data-zc-common') && el.value) {
        for (const id of this.selected) {
          const v = this.byId.get(id).variants.find(v => v.code === el.value);
          if (v) this.variants.set(id,v.id);
        }
        this.savePreferences(); this.render(); this.root.querySelector('[data-zc-common]')?.focus({preventScroll:true});
        this.announce(this.t('price_note'));
      }
    }
    picture(p) { return `<div class="zc-picture" style="--zc-image-scale:${p.imageScale}"><img src="${escape(p.image)}" alt="${escape(p.title)}" width="700" height="700" loading="lazy"></div>`; }
    renderPicker() {
      this.root.querySelector('[data-zc-filters]').innerHTML = ['all','classic','door','shelf','wc'].map(k => `<button type="button" data-zc-filter="${k}" aria-pressed="${this.filter === k}">${escape(this.t(k))}</button>`).join('');
      const query = normalize(this.query).replace(/\bx\b/g,' ').replace(/\s+/g,' ').trim();
      const candidates = this.products.filter(p => (this.filter === 'all' || this.filter === p.spec.kind) && normalize(`${p.title} ${p.spec.name} ${p.spec.format} ${this.t(p.spec.kind)}`).includes(query));
      this.root.querySelector('[data-zc-options]').innerHTML = candidates.length ? candidates.map(p => {
        const selected = this.selected.includes(p.id);
        return `<button type="button" class="zc-option" data-zc-toggle="${p.id}" aria-pressed="${selected}" ${!selected && this.selected.length >= 5 ? 'disabled' : ''}>${this.picture(p)}<span class="zc-option-name">${escape(this.name(p))}</span><span>${escape(this.format(p))}</span><span class="zc-option-action">${selected ? '✓ ' + escape(this.t('selected')) : '+ ' + escape(this.t('add_item'))}</span></button>`;
      }).join('') : `<div class="zc-no-results"><p>${escape(this.t('no_results'))}</p><button type="button" class="zc-text-button" data-zc-reset-search>${escape(this.t('reset_search'))}</button></div>`;
      this.root.querySelector('[data-zc-picker-count]').textContent = this.t('count',{count:this.selected.length});
      this.root.querySelector('[data-zc-limit]').hidden = this.selected.length < 5;
    }
    card(p, lowest, count) {
      const v = this.variant(p);
      const delta = count > 1 ? (v.price === lowest ? this.t('lowest') : this.t('extra',{amount:this.money(v.price-lowest)})) : '';
      return `<th scope="col" class="zc-product" data-zc-product="${p.id}"><button type="button" class="zc-remove" data-zc-remove="${p.id}" aria-label="${escape(this.t('remove_name',{name:p.title}))}">× <span>${escape(this.t('remove'))}</span></button>
        ${this.picture(p)}<span class="zc-model-view">${escape(this.t('model_view'))}</span><p class="zc-family">${escape(this.t(p.spec.kind))}</p><h2>${escape(this.name(p))}</h2><p class="zc-format">${escape(this.format(p))}</p>
        <label class="zc-variant-label"><span>${escape(this.t('finish'))}</span><select data-zc-variant="${p.id}" aria-label="${escape(this.t('finish') + ' — ' + p.title)}">${p.variants.map(x => `<option value="${x.id}" ${x.id === v.id ? 'selected' : ''}>${escape(x.name)}</option>`).join('')}</select></label>
        <p class="zc-price">${escape(this.money(v.price))}</p><p class="zc-delta">${escape(delta)}</p><p class="zc-stock ${v.available ? '' : 'zc-stock-unavailable'}">${escape(this.t(v.available ? 'available' : 'unavailable'))}</p>
        <a class="zc-button" href="${escape(this.url(p))}">${escape(this.t('choose'))} <span aria-hidden="true">↗</span></a><a class="zc-3d-link" href="${escape(this.url(p,true))}">${escape(this.t('three_d'))} <span aria-hidden="true">↗</span></a></th>`;
    }
    silhouette(p, list) {
      const displayHeight = x => x.spec.kind === 'shelf' ? x.spec.depth : x.spec.height;
      const scale = Math.min(166 / Math.max(...list.map(x=>x.spec.width)),112 / Math.max(...list.map(displayHeight)));
      const w = p.spec.width * scale, h = displayHeight(p) * scale, x = (190-w)/2, y = 124-h;
      const shelf = p.spec.kind === 'shelf';
      const shape = shelf ? `<path d="M${x},${y+h} L${x},${y} L${x+w*.72},${y} L${x+w},${y+h*.6} L${x+w},${y+h} Z"/>` : `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="1"/>`;
      const lines = shelf ? '' : p.spec.dividers.map(a=>`<path d="M${x},${124-a*scale}H${x+w}" class="zc-diagram-line"/>`).join('');
      return `<svg viewBox="0 0 190 140" class="zc-diagram" role="img" aria-label="${escape(p.title+' — '+this.t(shelf?'top':'front'))}"><path d="M8 124H182" class="zc-diagram-ground"/>${shape}${lines}</svg><span class="zc-view-label">${escape(this.t(shelf?'top':'front'))}</span>`;
    }
    render() {
      const scroll = this.app.querySelector('.zc-table-scroll')?.scrollLeft || 0;
      const list = this.selected.map(id=>this.byId.get(id));
      const count = list.length;
      const t = (key,vars) => escape(this.t(key,vars));
      let content = `<div class="zc-toolbar"><div><span class="zc-selection-label">${t('selection')}</span><strong>${t('count',{count})}</strong></div><button type="button" class="zc-add" data-zc-open>+ ${t('add')}</button></div>`;
      if (!count) {
        content += `<div class="zc-empty"><span class="zc-empty-icon" aria-hidden="true">↔</span><h2>${t('empty_title')}</h2><p>${t('empty_text')}</p><button type="button" class="zc-button" data-zc-open>${t('add')}</button></div>`;
      } else {
        const common = list[0].variants.filter(v=>list.every(p=>p.variants.some(x=>x.code===v.code)));
        const current = this.variant(list[0]).code;
        const shared = list.every(p=>this.variant(p).code===current) ? current : '';
        content += `<div class="zc-controls"><label class="zc-difference-toggle"><input type="checkbox" data-zc-differences ${this.differences?'checked':''} ${count<2?'disabled':''}>${t('differences')}</label><label class="zc-common-label">${t('same_finish')}<select data-zc-common ${count<2?'disabled':''}><option value="">${t('individual')}</option>${common.map(v=>`<option value="${escape(v.code)}" ${shared===v.code?'selected':''}>${escape(v.name)}</option>`).join('')}</select></label><div class="zc-units" role="group" aria-label="${t('units')}">${['in','mm'].map(u=>`<button type="button" data-zc-unit="${u}" aria-pressed="${this.unit===u}">${t(u==='in'?'inches':'mm')}</button>`).join('')}</div></div>`;
        if (count===1) content += `<p class="zc-one">${t('one')}</p>`;
        content += `<p class="zc-price-note">${t('price_note')} ${escape(this.data.currency)}</p><p class="zc-scroll-hint">${t('scroll')} <span aria-hidden="true">↔</span></p>`;
        const lowest = Math.min(...list.map(p=>this.variant(p).price));
        content += `<div class="zc-table-scroll" tabindex="0" role="region" aria-label="${t('selection')}"><table class="zc-table" style="--zc-count:${count}"><caption class="zc-sr">${t('page_title')}</caption><colgroup><col class="zc-label-col">${list.map(()=>'<col>').join('')}</colgroup><thead><tr><th scope="col" class="zc-row-label zc-table-corner"><span>${t('selection')}</span></th>${list.map(p=>this.card(p,lowest,count)).join('')}</tr></thead><tbody>`;
        const rows = [
          ['fit',p=>this.t(p.spec.fitKey)],
          ['storage',p=>this.t(p.spec.storageKey)],
          ['installation',p=>this.t(p.spec.kind==='shelf'?'surface':'recessed')],
          ['width',p=>this.measure(p.spec.width)],['height',p=>this.measure(p.spec.height)],['depth',p=>this.measure(p.spec.depth)],
          ['rough',p=>p.spec.roughWidth===null?this.t('no_cutout'):`${this.measure(p.spec.roughWidth)} × ${this.measure(p.spec.roughHeight)}`],
          ['wall',p=>p.spec.wall===null?this.t('no_recess'):this.measure(p.spec.wall)],
          ['finishes',p=>[...p.variants].sort((a,b)=>a.code.localeCompare(b.code)).map(v=>v.name).join(' · ')]
        ];
        let hidden = 0;
        for (const [key,value] of rows) {
          const values=list.map(value), different=new Set(values).size>1;
          if (this.differences && count>1 && !different) {hidden++;continue;}
          content += `<tr data-zc-row="${key}" class="${different?'zc-different':''}"><th scope="row" class="zc-row-label">${t(key)}${different?'<span class="zc-diff-mark" aria-hidden="true"></span>':''}</th>${values.map(v=>`<td>${escape(v)}</td>`).join('')}</tr>`;
        }
        content += `<tr class="zc-scale-row"><th scope="row" class="zc-row-label">${t('scale')}<small>${t('scale_note')}</small></th>${list.map(p=>`<td>${this.silhouette(p,list)}</td>`).join('')}</tr>`;
        content += `<tr class="zc-doc-row"><th scope="row" class="zc-row-label">${t('documents')}</th>${list.map(p=>`<td><a href="${escape(p.spec.specUrl)}" target="_blank" rel="noopener">${t('plans')} <span aria-hidden="true">↗</span></a><a href="${escape(p.spec.manualUrl)}" target="_blank" rel="noopener">${t('manual')} <span aria-hidden="true">↗</span></a></td>`).join('')}</tr></tbody></table></div>`;
        if (hidden) content += `<p class="zc-hidden-note">${t('hidden_rows',{count:hidden})}${hidden===rows.length?' '+t('all_same'):''}</p>`;
        content += `<div class="zc-footnotes"><p>${t('footnote')}</p>${list.some(p=>p.spec.kind==='wc')?`<p>${t('wc_note')}</p>`:''}</div>`;
      }
      content += `<aside class="zc-next"><div><h2>${t('next_title')}</h2><p>${t('next_text')}</p></div><div><a href="${escape(this.data.finishesUrl)}">${t('finish_guide')} ↗</a><a href="${escape(this.data.retailer)}">${t('retailer')} ↗</a></div></aside>`;
      this.app.innerHTML = content;
      const scroller=this.app.querySelector('.zc-table-scroll');
      if (scroller) scroller.scrollLeft=scroll;
      if (this.dialog.open) this.renderPicker();
    }
  }
  const init = scope => scope.querySelectorAll('[data-zc]:not([data-zc-initialized])').forEach(root => {
    root.dataset.zcInitialized='true';
    try { new ZveltComparison(root); } catch { root.querySelector('[data-zc-app]').hidden=true; root.querySelector('[data-zc-fallback]').hidden=false; }
  });
  init(document);
  document.addEventListener('shopify:section:load',e=>init(e.target));
})();
